import { useState } from "react";
import { Head, Link, router, useForm } from "@inertiajs/react";
import { Download, Clock, RotateCcw, Pencil, Trash2, CheckCircle2, Circle, ExternalLink, X, UserPlus, Settings, Users, Trophy, Upload, Award } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/EmptyState";
import FormField from "@/components/forms/FormField";
import PrizeTrackerCard from "@/components/schools/PrizeTrackerCard";
import StudentNameGuidance from "@/components/schools/StudentNameGuidance";
import { DataTable, Th, Td, rowClass } from "@/components/shared/DataTable";
import { useConfirm } from "@/components/shared/ConfirmProvider";
import { formatRoom, formatGrade } from "@/lib/classroom";
import { formatMoney, formatNumber, pluralize } from "@/lib/format";

/**
 * Admin classroom page — student roster with management actions.
 * Classroom details + teachers are edited on the separate Edit page.
 */
export default function Show({
  school, membership, classroom, campaign, students,
  student_prizes = [],
  add_student_path, edit_path, milestones_path, log_minutes_path, take_home_sheets_path,
  congratulations_certificates_path, import_path, back,
}) {
  const [adding, setAdding] = useState(false);
  const teacherNames = classroom.teachers?.map((t) => t.name || t).join(", ");
  // Certificates are for the end of the read-a-thon.
  const finished = campaign.state === "grace" || campaign.state === "locked";

  return (
    <SchoolLayout
      school={school}
      membership={membership}
      back={back}
      eyebrow={`${formatGrade(classroom.grade) || "Classroom"} · ${teacherNames || "No teacher yet"}`}
      title={formatRoom(classroom.name)}
      actions={
        <>
          {campaign.accepting_minutes && (
            <Button size="sm" asChild>
              <Link href={log_minutes_path}><Clock className="h-4 w-4" /> Log minutes</Link>
            </Button>
          )}
          {milestones_path && (
            <Button variant="outline" size="sm" asChild>
              <Link href={milestones_path}><Trophy className="h-4 w-4" /> Goal & rewards</Link>
            </Button>
          )}
          <Button variant="outline" size="sm" asChild>
            <Link href={edit_path}><Settings className="h-4 w-4" /> Edit classroom</Link>
          </Button>
        </>
      }
    >
      <Head title={`${formatRoom(classroom.name)} · Students`} />

      {students.length === 0 && !adding ? (
        <EmptyState
          icon={Users}
          title="No students yet"
          description="Import a class list, or add students one at a time."
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Button asChild><Link href={import_path}><Upload className="h-4 w-4" /> Import a class list</Link></Button>
              <Button variant="outline" onClick={() => setAdding(true)}><UserPlus className="h-4 w-4" /> Add a student</Button>
            </div>
          }
        />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {pluralize(students.length, "student")} · {formatNumber(students.filter((s) => s.claimed).length)} activated
            </p>
            <div className="flex flex-wrap gap-2">
              {!adding && (
                <Button variant="outline" size="sm" onClick={() => setAdding(true)}><UserPlus className="h-4 w-4" /> Add student</Button>
              )}
              {take_home_sheets_path && students.length > 0 && (
                <Button variant="outline" size="sm" asChild>
                  <a href={take_home_sheets_path}><Download className="h-4 w-4" /> Take-home sheets</a>
                </Button>
              )}
              {finished && congratulations_certificates_path && students.length > 0 && (
                <Button variant="outline" size="sm" asChild>
                  <a href={congratulations_certificates_path}><Award className="h-4 w-4" /> Certificates</a>
                </Button>
              )}
            </div>
          </div>

          {adding && <AddStudentForm addPath={add_student_path} onClose={() => setAdding(false)} />}

          {students.length > 0 && (
            <DataTable>
              <thead>
                <tr>
                  <Th>Student</Th>
                  <Th align="center">Activated</Th>
                  <Th>Activation code</Th>
                  <Th align="right">Raised</Th>
                  <Th align="right">Minutes</Th>
                  <Th align="right" srOnly>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => <StudentRow key={s.id} student={s} finished={finished} />)}
              </tbody>
            </DataTable>
          )}
        </div>
      )}

      <div className="mt-8">
        <PrizeTrackerCard prizes={student_prizes} />
      </div>
    </SchoolLayout>
  );
}

function StudentRow({ student, finished }) {
  const confirm = useConfirm();
  const [editingName, setEditingName] = useState(false);
  const { data, setData, patch, processing, transform } = useForm({
    first_name: student.first_name,
    last_name: "",
  });

  const onSaveName = (e) => {
    e.preventDefault();
    transform(({ first_name, last_name }) => (
      last_name.trim() ? { first_name, last_name } : { first_name }
    ));
    patch(student.update_path, {
      preserveScroll: true,
      onSuccess: () => { setData("last_name", ""); setEditingName(false); },
    });
  };
  const toggleNameEditor = (open) => {
    setData({ first_name: student.first_name, last_name: "" });
    setEditingName(open);
  };
  const onRotate = async () => {
    const ok = await confirm({
      title: `New activation code for ${student.first_name}?`,
      body: "The old code stops working. Families who already activated keep their access. Print a new take-home sheet afterwards.",
      confirmLabel: "Make a new code",
    });
    if (ok) router.post(student.rotate_code_path, {}, { preserveScroll: true });
  };
  const onDelete = async () => {
    const ok = await confirm({
      title: `Remove ${student.full_name}?`,
      body: "Their reading page and logged minutes are deleted. This can't be undone.",
      confirmLabel: `Remove ${student.first_name}`,
      destructive: true,
    });
    if (ok) router.delete(student.delete_path, { preserveScroll: true });
  };

  return (
    <tr className={rowClass}>
      <Td>
        {editingName ? (
          <form onSubmit={onSaveName} className="max-w-xl space-y-3">
            <StudentNameGuidance id={`student-name-guidance-${student.id}`} />
            <div className="flex flex-wrap items-end gap-2">
              <FormField id={`first-name-${student.id}`} name="first_name" label="Full first name" required value={data.first_name} onChange={(e) => setData("first_name", e.target.value)} autoFocus aria-describedby={`student-name-guidance-${student.id}`} />
              <FormField id={`last-name-${student.id}`} name="last_name" label="Full last name (if changing)" value={data.last_name} onChange={(e) => setData("last_name", e.target.value)} placeholder={`Leave blank to keep ${student.last_name}`} aria-describedby={`student-name-guidance-${student.id}`} />
              <Button type="submit" size="sm" disabled={processing}>{processing ? "Saving…" : "Save"}</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => toggleNameEditor(false)}>Cancel</Button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex items-center gap-1">
              <Link href={student.student_path} className="font-medium text-foreground hover:text-primary">{student.full_name}</Link>
              <IconAction label={`Edit ${student.full_name}'s name`} onClick={() => toggleNameEditor(true)} icon={Pencil} />
            </div>
            {/* The /p/<slug> URL 404s for unclaimed students by design
                (BUSINESS_RULES.md §1 — non-enumerable URLs). Only show
                the link once the family has claimed the student. */}
            {student.claimed && (
              <a href={`/p/${student.public_slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-xs text-primary hover:underline">
                Donation page <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            )}
          </>
        )}
      </Td>
      <Td align="center">
        {student.claimed
          ? <CheckCircle2 className="inline h-4 w-4 text-success" aria-label="Activated" />
          : <Circle className="inline h-4 w-4 text-muted-foreground/40" aria-label="Not activated yet" />}
      </Td>
      <Td>
        <code className="rounded bg-secondary/60 px-2 py-1 font-mono text-xs">{student.claim_code}</code>
      </Td>
      <Td align="right">{formatMoney(student.raised_cents)}</Td>
      <Td align="right" className="text-muted-foreground">{formatNumber(student.minutes_logged)}</Td>
      <Td align="right">
        <div className="flex items-center justify-end gap-0.5">
          {student.sheet_path && <IconAction label={`Download ${student.first_name}'s take-home sheet`} href={student.sheet_path} icon={Download} />}
          {finished && student.certificate_path && <IconAction label={`Download ${student.first_name}'s certificate`} href={student.certificate_path} icon={Award} />}
          <IconAction label={`New activation code for ${student.first_name}`} onClick={onRotate} icon={RotateCcw} />
          <IconAction label={`Remove ${student.full_name}`} onClick={onDelete} icon={Trash2} destructive />
        </div>
      </Td>
    </tr>
  );
}

// A 36px icon button with a real label (and a tooltip saying the same).
function IconAction({ label, icon: Icon, onClick, href, destructive = false }) {
  const className = `inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
    destructive ? "hover:bg-destructive/10 hover:text-destructive" : "hover:bg-secondary/60 hover:text-foreground"
  }`;
  const icon = <Icon className="h-4 w-4" aria-hidden="true" />;
  return href
    ? <a href={href} className={className} aria-label={label} title={label}>{icon}</a>
    : <button type="button" onClick={onClick} className={className} aria-label={label} title={label}>{icon}</button>;
}

function AddStudentForm({ addPath, onClose }) {
  const { data, setData, post, processing, reset, errors } = useForm({ first_name: "", last_name: "" });

  const submit = (e) => {
    e.preventDefault();
    // Stays open for the next student; the list updates underneath.
    post(addPath, { preserveScroll: true, onSuccess: () => reset() });
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border border-border bg-card p-4">
      <StudentNameGuidance id="add-student-name-guidance" />
      <div className="flex flex-wrap items-end gap-3">
        <FormField name="first_name" label="Full first name" required autoFocus placeholder="Sarah" value={data.first_name} onChange={(e) => setData("first_name", e.target.value)} error={errors.first_name} aria-describedby="add-student-name-guidance" />
        <FormField name="last_name" label="Full last name" required placeholder="Thompson" value={data.last_name} onChange={(e) => setData("last_name", e.target.value)} error={errors.last_name} aria-describedby="add-student-name-guidance" />
        <Button type="submit" disabled={processing || !data.first_name || !data.last_name}>{processing ? "Adding…" : "Add student"}</Button>
        <Button type="button" variant="ghost" onClick={onClose}><X className="h-4 w-4" /> Done</Button>
      </div>
    </form>
  );
}
